/**
 * care-set-nudge.js — Amaroq Glamping (.dk)
 * ------------------------------------------------------------------
 * Gently nudges customers to add the size-matched Care Set when they
 * add a tent to their cart. Renders INLINE inside the slide-in cart
 * drawer, just above the "Ofte købt med" (cart-recommendations)
 * block, rather than as a floating popup.
 *
 * HOW IT WORKS
 * 1. Watches for successful /cart/add(.js) requests (both fetch and
 *    XHR, since different Shopify themes/apps use either).
 * 2. Checks whether the item just added is a tent (via variant ID
 *    for Emperor, since Emperor L/XL share one product handle; via
 *    handle for every other tent).
 * 3. Waits briefly (SHOW_DELAY_MS), then waits for the drawer's
 *    <cart-recommendations> custom element to exist in the DOM
 *    (it's re-rendered by the theme after every cart change, so we
 *    poll for it rather than assuming a fixed timing).
 * 4. Looks up the matching Care Set product for that size (each size
 *    is its own product) and checks it isn't already in the cart.
 * 5. If not, inserts our block directly before <cart-recommendations>,
 *    styled with the theme's own CSS variables so it reads as a
 *    native part of the drawer, not an injected popup.
 * 6. "Add to cart" on the block adds the matched Care Set via the
 *    Ajax Cart API and dispatches a cart:refresh event.
 *
 * INSTALL
 * 1. Save this file to assets/care-set-nudge.js in an UNPUBLISHED
 *    theme copy (Online Store → Themes → your copy → Edit code →
 *    Assets → Add a new asset).
 * 2. In layout/theme.liquid, right before </body>, add:
 *      <script src="{{ 'care-set-nudge.js' | asset_url }}" defer></script>
 * 3. Preview the theme copy, add a tent to cart, open the cart
 *    drawer, confirm the block appears above "Ofte købt med" with
 *    the correct Care Set. Then publish.
 *
 * NOTE — this file is configured for the .dk store only. The .se/
 * .de/.fr/.com stores use different handles and variant IDs, so
 * duplicate this file and adjust CONFIG for each of them separately.
 *
 * Horizon L / Horizon XL don't have a Care Set yet — they're left in
 * the tent map (so sizes are recognized) but have no entry in
 * CARE_SET_HANDLE_BY_SIZE, so the nudge simply won't fire for them
 * until you add one. Just add the two missing lines when it exists.
 *
 * If the drawer never contains a <cart-recommendations> element
 * (e.g. it was removed from the theme later), the script simply
 * never finds an anchor and stays silent — it won't throw errors or
 * fall back to a floating popup.
 * ------------------------------------------------------------------
 */

(function () {
  "use strict";

  // ==================================================================
  // CONFIG — Amaroq Glamping .dk
  // ==================================================================

  const CONFIG = {
    // Tents whose SIZE can be told apart by product handle alone.
    TENT_HANDLE_SIZE_MAP: {
      "shelter-telt": "Shelter",
      "spejdertelt": "Spejder",
      "amaroq-glampingtelt-small": "Small",
      "amaroq-glampingtelt-medium": "Medium",
      "amaroq-glampingtelt-large": "Large",
      "amaroq-glampingtelt-xl": "XL",
      "amaroq-glampingtelt-xxl-1": "XXL",
      "panorama-large": "Horizon L",
      "panorama-xl": "Horizon XL",
      // "amaroq-glamping-emperor" intentionally NOT listed here —
      // Emperor L and XL share this same handle, so they're
      // distinguished by variant ID instead (see below).
    },

    // Tents that need VARIANT-level distinction because two sizes
    // share the same product handle (currently: Emperor).
    TENT_VARIANT_SIZE_MAP: {
      53708249760084: "Emperor L",
      53708249792852: "Emperor XL",
    },

    // Each Care Set size is its own separate product. Map size ->
    // Care Set product handle. Assumes each Care Set product has a
    // single variant; the script looks that variant up automatically.
    CARE_SET_HANDLE_BY_SIZE: {
      Shelter: "plejesaet-shelter-footprint-og-regnslag",
      Spejder: "plejesaet-spejder-footprint-og-regnslag",
      Small: "plejesaet-small-footprint-og-regnslag",
      Medium: "plejesaet-medium-footprint-og-regnslag",
      Large: "plejesaet-large-footprinr-og-regnslag",
      XL: "plejesaet-xl-footprint-regnslag",
      XXL: "plejesaet-xxl-regnslag-footpring",
      "Emperor L": "plejesaet-emperor-footprint-og-regnslag",
      "Emperor XL": "plejesaet-emperor-xl",
      // "Horizon L" / "Horizon XL": not created yet — add here once
      // they exist, e.g. "Horizon L": "plejesaet-horizon-l-handle"
    },

    TEXT: {
      heading: "Forlæng dit telts levetid",
      body: (tentName, size) =>
        `${size} Plejesættet beskytter dit ${tentName} mod vejr og slid, så det holder år efter år.`,
      addButton: "Læg i kurv",
      dismissButton: "Nej tak",
      addedConfirmation: "Tilføjet",
    },

    // How long (ms) after the tent is added before we start looking
    // for the drawer to inject into. A short pause feels calmer than
    // popping in the instant the drawer opens.
    SHOW_DELAY_MS: 1000,

    // How long (ms) to keep polling for <cart-recommendations> to
    // appear in the DOM before giving up silently.
    ANCHOR_WAIT_TIMEOUT_MS: 5000,
    ANCHOR_WAIT_INTERVAL_MS: 150,

    // Don't show the nudge again in this browser session once
    // dismissed (per size), so it isn't naggy on repeat visits.
    REMEMBER_DISMISSAL: true,
  };

  // ==================================================================
  // Internals — shouldn't need to edit below this line.
  // ==================================================================

  const SESSION_KEY_PREFIX = "care-set-nudge-dismissed:";
  const BLOCK_CLASS = "care-set-nudge-inline";

  function wasDismissed(size) {
    if (!CONFIG.REMEMBER_DISMISSAL) return false;
    try {
      return sessionStorage.getItem(SESSION_KEY_PREFIX + size) === "1";
    } catch (e) {
      return false;
    }
  }

  function markDismissed(size) {
    if (!CONFIG.REMEMBER_DISMISSAL) return;
    try {
      sessionStorage.setItem(SESSION_KEY_PREFIX + size, "1");
    } catch (e) {
      /* ignore */
    }
  }

  function sizeForAddedItem(item) {
    if (!item) return null;
    // Variant-level overrides first (handles Emperor L vs XL).
    if (item.variant_id && CONFIG.TENT_VARIANT_SIZE_MAP[item.variant_id]) {
      return CONFIG.TENT_VARIANT_SIZE_MAP[item.variant_id];
    }
    // Fall back to handle-based lookup for everything else.
    const handle = item.handle;
    if (!handle) return null;
    if (CONFIG.TENT_HANDLE_SIZE_MAP[handle]) return CONFIG.TENT_HANDLE_SIZE_MAP[handle];
    return null;
  }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }

  function cartHasVariant(cart, variantId) {
    if (!variantId) return false;
    return cart.items.some((item) => item.variant_id === variantId);
  }

  // Care Sets are separate products (one per size), each assumed to
  // have a single variant. Looks the product up by handle and
  // returns its first variant's details.
  async function getCareSetForSize(size) {
    const handle = CONFIG.CARE_SET_HANDLE_BY_SIZE[size];
    if (!handle) return null; // no Care Set for this size yet

    const res = await fetch(`/products/${handle}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const product = await res.json();
    const variant = product.variants[0];
    if (!variant) return null;

    return {
      title: product.title,
      image: (variant.featured_image && variant.featured_image.src) || product.featured_image,
      price: variant.price,
      currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : "",
      url: `/products/${product.handle}?variant=${variant.id}`,
      variantId: variant.id,
    };
  }

  function formatMoney(cents, currency) {
    const amount = (cents / 100).toLocaleString("da-DK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? `${amount} ${currency}` : amount;
  }

  // Polls for a selector to appear in the DOM, since the drawer (and
  // its <cart-recommendations> element) is re-rendered asynchronously
  // after cart changes rather than on a fixed schedule.
  function waitForElement(selector, timeoutMs, intervalMs) {
    return new Promise((resolve) => {
      const start = Date.now();
      (function check() {
        const el = document.querySelector(selector);
        if (el) return resolve(el);
        if (Date.now() - start > timeoutMs) return resolve(null);
        setTimeout(check, intervalMs);
      })();
    });
  }

  // ---- UI ----------------------------------------------------------

  let styleInjected = false;
  function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    const style = document.createElement("style");
    style.textContent = `
      .${BLOCK_CLASS} {
        margin: 0.875rem var(--sidebar-gutter, 1.25rem);
        padding: 0.875rem;
        background: var(--color-secondary-background-main, #f4f2ec);
        border: 1px solid var(--color-borders-main, #e5e5e5);
        border-radius: 10px;
        display: flex;
        gap: 0.75rem;
        align-items: flex-start;
        position: relative;
      }
      .${BLOCK_CLASS}__close {
        position: absolute;
        top: 0.625rem;
        right: 0.625rem;
        background: none;
        border: none;
        font-size: 16px;
        line-height: 1;
        color: var(--color-text-main, #999);
        opacity: 0.5;
        cursor: pointer;
        padding: 0;
      }
      .${BLOCK_CLASS}__image {
        width: 3.75rem;
        height: 3.75rem;
        object-fit: cover;
        border-radius: 4px;
        flex-shrink: 0;
        background: var(--color-secondary-background-main, #f2f2f2);
      }
      .${BLOCK_CLASS}__content {
        flex: 1;
        min-width: 0;
        padding-right: 1rem;
      }
      .${BLOCK_CLASS}__heading {
        font-weight: var(--font-weight-body-primary-bold, 600);
        font-size: calc(13px / 16 * var(--base-body-primary-size, 16px));
        margin: 0 0 0.25rem;
      }
      .${BLOCK_CLASS}__body {
        font-size: calc(13px / 16 * var(--base-body-primary-size, 16px));
        line-height: 1.4;
        margin: 0 0 0.5rem;
        opacity: 0.75;
      }
      .${BLOCK_CLASS}__actions {
        display: flex;
        align-items: center;
        gap: 0.625rem;
      }
      .${BLOCK_CLASS}__price {
        font-weight: var(--font-weight-body-primary-bold, 600);
        font-size: calc(13px / 16 * var(--base-body-primary-size, 16px));
      }
      .${BLOCK_CLASS}__add {
        border: 1px solid var(--color-text-main, #1a1a1a);
        border-radius: var(--border-radius-forms, 6px);
        background: var(--color-text-main, #1a1a1a);
        color: var(--color-body-background-main, #fff);
        padding: 0.3rem 0.75rem;
        font-size: calc(13px / 16 * var(--base-body-primary-size, 16px));
        cursor: pointer;
      }
      .${BLOCK_CLASS}__dismiss {
        background: none;
        border: none;
        font-size: calc(13px / 16 * var(--base-body-primary-size, 16px));
        opacity: 0.6;
        cursor: pointer;
        padding: 0.3rem 0;
      }
    `;
    document.head.appendChild(style);
  }

  function removeExistingBlock() {
    const existing = document.querySelector("." + BLOCK_CLASS);
    if (existing) existing.remove();
  }

  function showInline(anchorEl, { tentName, size, careSet }) {
    injectStyles();
    removeExistingBlock();

    const block = document.createElement("div");
    block.className = BLOCK_CLASS;
    block.setAttribute("role", "note");
    block.setAttribute("aria-label", CONFIG.TEXT.heading);

    block.innerHTML = `
      <button class="${BLOCK_CLASS}__close" aria-label="Luk">&times;</button>
      <img class="${BLOCK_CLASS}__image" src="${careSet.image || ""}" alt="${careSet.title}">
      <div class="${BLOCK_CLASS}__content">
        <p class="${BLOCK_CLASS}__heading">${CONFIG.TEXT.heading}</p>
        <p class="${BLOCK_CLASS}__body">${CONFIG.TEXT.body(tentName, size)}</p>
        <div class="${BLOCK_CLASS}__actions">
          <span class="${BLOCK_CLASS}__price">${formatMoney(careSet.price, careSet.currency)}</span>
          <button class="${BLOCK_CLASS}__add">${CONFIG.TEXT.addButton}</button>
          <button class="${BLOCK_CLASS}__dismiss">${CONFIG.TEXT.dismissButton}</button>
        </div>
      </div>
    `;

    anchorEl.insertAdjacentElement("beforebegin", block);

    function remove() {
      block.remove();
    }

    block.querySelector(`.${BLOCK_CLASS}__close`).addEventListener("click", () => {
      markDismissed(size);
      remove();
    });
    block.querySelector(`.${BLOCK_CLASS}__dismiss`).addEventListener("click", () => {
      markDismissed(size);
      remove();
    });
    block.querySelector(`.${BLOCK_CLASS}__add`).addEventListener("click", async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.textContent = "…";
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: careSet.variantId, quantity: 1 }] }),
        });
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.body.dispatchEvent(new CustomEvent("cart:refresh"));
        btn.textContent = CONFIG.TEXT.addedConfirmation;
        setTimeout(remove, 1000);
      } catch (err) {
        btn.disabled = false;
        btn.textContent = CONFIG.TEXT.addButton;
        console.error("care-set-nudge: failed to add care set", err);
      }
    });
  }

  // ---- Trigger: watch for tent additions ----------------------------

  function handleItemAdded(item) {
    const size = sizeForAddedItem(item);
    if (!size) return; // not a tent we recognize
    if (wasDismissed(size)) return;

    setTimeout(async () => {
      const careSet = await getCareSetForSize(size);
      if (!careSet) return; // no Care Set configured for this size yet
      if (wasDismissed(size)) return;

      const cart = await getCart();
      if (cartHasVariant(cart, careSet.variantId)) return; // already in cart

      const anchor = await waitForElement(
        "cart-recommendations",
        CONFIG.ANCHOR_WAIT_TIMEOUT_MS,
        CONFIG.ANCHOR_WAIT_INTERVAL_MS
      );
      if (!anchor) return; // drawer/recommendations block never appeared — stay silent

      if (wasDismissed(size)) return; // re-check in case dismissed while waiting

      showInline(anchor, { tentName: item.product_title || item.title, size, careSet });
    }, CONFIG.SHOW_DELAY_MS);
  }

  // Intercept fetch() calls to /cart/add(.js)
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === "string" ? args[0] : args[0] && args[0].url;
      if (url && /\/cart\/add(\.js)?(\?|$)/.test(url) && response.ok) {
        const clone = response.clone();
        clone.json().then((data) => {
          const items = data.items ? data.items : [data];
          items.forEach((item) => item && item.handle && handleItemAdded(item));
        }).catch(() => {});
      }
    } catch (e) {
      /* never break the theme's own cart handling */
    }
    return response;
  };

  // Intercept XHR-based add-to-cart, for themes/apps that don't use fetch
  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.addEventListener("load", function () {
      try {
        if (/\/cart\/add(\.js)?(\?|$)/.test(url) && this.status >= 200 && this.status < 300) {
          const data = JSON.parse(this.responseText);
          const items = data.items ? data.items : [data];
          items.forEach((item) => item && item.handle && handleItemAdded(item));
        }
      } catch (e) {
        /* ignore */
      }
    });
    return originalXHROpen.call(this, method, url, ...rest);
  };
})();